import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export function CanonicalPageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="ui-module-header">
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="ui-module-header__actions">{actions}</div> : null}
    </header>
  );
}

export function CanonicalFilterBar({ children }: { children: ReactNode }) {
  return <section className="ui-filter-bar" aria-label="Filtros">{children}</section>;
}
export function CanonicalFilterField({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="ui-filter-field">
      <span className="ui-filter-field__icon" aria-hidden="true">{icon}</span>
      {children}
    </div>
  );
}

export function CanonicalActionMenu({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;
    const trigger = rootRef.current.querySelector<HTMLButtonElement>('.ui-row-menu__trigger');
    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const panelWidth = panelRef.current?.offsetWidth || 168;
    const panelHeight = panelRef.current?.offsetHeight || 96;
    const viewportGap = 8;
    const menuGap = 6;
    const left = Math.min(
      Math.max(viewportGap, triggerRect.right - panelWidth),
      window.innerWidth - panelWidth - viewportGap,
    );
    const opensUp = triggerRect.bottom + menuGap + panelHeight + viewportGap > window.innerHeight;
    const top = opensUp ? triggerRect.top - panelHeight - menuGap : triggerRect.bottom + menuGap;
    setPanelPosition({ top: Math.max(viewportGap, top), left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !rootRef.current?.contains(event.target) &&
        !panelRef.current?.contains(event.target)
      ) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const closeOnViewportChange = () => setOpen(false);
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', closeOnViewportChange);
    document.addEventListener('scroll', closeOnViewportChange, true);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', closeOnViewportChange);
      document.removeEventListener('scroll', closeOnViewportChange, true);
    };
  }, [open]);

  return (
    <div className="ui-row-menu" data-open={open || undefined} ref={rootRef}>
      <button
        type="button"
        className="ui-row-action ui-row-menu__trigger"
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {icon}
      </button>
      {open ? createPortal(
        <div
          className="ui-row-menu__panel"
          role="menu"
          ref={panelRef}
          style={panelPosition}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}


export function CanonicalCard({
  title,
  icon,
  children,
  className = '',
}: {
  title?: string;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`ui-canonical-card${className ? ` ${className}` : ''}`}>
      {title ? <h2>{icon ? <span className="ui-canonical-card__icon">{icon}</span> : null}{title}</h2> : null}
      {children}
    </section>
  );
}

export function CanonicalStatus({
  tone,
  children,
}: {
  tone: 'success' | 'warning' | 'neutral' | 'danger';
  children: ReactNode;
}) {
  return <span className={`ui-canonical-status ui-canonical-status--${tone}`}>{children}</span>;
}
